import React, { useEffect, useState } from "react";
import { Modal, Spin } from "antd";
import Chart from "react-apexcharts";
import Papa from "papaparse";

const ClusterModal = ({ isVisible, onClose, acId }) => {
  const [chartData, setChartData] = useState([]);
  const [outChartData, setOutChartData] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isVisible && acId) {
      setLoading(true);
      Promise.all([fetchChartData(acId), fetchOutChartData(acId)]).finally(() =>
        setLoading(false)
      );
    }
  }, [isVisible, acId]);

  const fetchChartData = async (acId) => {
    try {
      const response = await fetch("/all_data.csv");
      const text = await response.text();
      const parsedData = Papa.parse(text, {
        header: true,
        skipEmptyLines: true,
      }).data;

      const acIdData = parsedData.find((row) => row.AC_ID === acId);

      if (acIdData) {
        const consData = Object.keys(acIdData)
          .filter((key) => key.startsWith("CONS_"))
          .map((key) => ({
            label: key.replace("CONS_", ""),
            value: parseFloat(acIdData[key]) || 0,
          }))
          .sort((a, b) => parseInt(b.label) - parseInt(a.label)); // Sort descending

        setChartData(consData);
      }
    } catch (error) {
      console.error("Error fetching chart data:", error);
    }
  };

  const fetchOutChartData = async (acId) => {
    try {
      const [allDataResponse, mergedDataResponse] = await Promise.all([
        fetch("/all_data.csv"),
        fetch("/merged_data_sum_normalized.csv"),
      ]);

      const allDataText = await allDataResponse.text();
      const mergedDataText = await mergedDataResponse.text();

      const allData = Papa.parse(allDataText, {
        header: true,
        skipEmptyLines: true,
      }).data;
      const mergedData = Papa.parse(mergedDataText, {
        header: true,
        skipEmptyLines: true,
      }).data;

      const selectedRow = allData.find((row) => row.AC_ID === acId);
      if (!selectedRow) {
        console.error(`Row with AC_ID ${acId} not found in all_data.csv`);
        return;
      }
      const outValue = selectedRow.out;

      const mergedRow = mergedData.find((row) => row.out === outValue);
      if (!mergedRow) {
        console.error(
          `Row with out value ${outValue} not found in merged_data_sum_normalized.csv`
        );
        return;
      }

      const consData = Object.keys(mergedRow)
        .filter((key) => key.startsWith("CONS_"))
        .map((key) => ({
          label: key.replace("CONS_", ""),
          value: parseFloat(mergedRow[key]) || 0,
        }))
        .sort((a, b) => parseInt(b.label) - parseInt(a.label)); // Sort descending

      setOutChartData(consData);
    } catch (error) {
      console.error("Error fetching Out chart data:", error);
    }
  };

  const chartOptions = {
    chart: { id: "line-chart", toolbar: { show: false } },
    xaxis: { categories: chartData.map((d) => d.label) },
    markers: {
      size: 5,
      colors: "red", // Red markers
      strokeWidth: 2,
    },
    stroke: { curve: "smooth", width: 4 },
  };

  return (
    <Modal
      open={isVisible}
      onCancel={onClose}
      footer={null}
      centered
      width={900}
      title={
        <div className="modal-header justify-content-center">
          <h3>
            <b>Monthly Consumption Vs Cluster Graph</b>
          </h3>

          {/* <button
              onClick={onClose}
              className="btn-close position-absolute end-0"
            /> */}
        </div>
      }
    >
      {loading ? (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "300px",
          }}
        >
          <Spin size="large" />
        </div>
      ) : (
        <div className="row">
          <span style={{ marginBottom: "20px" }}>
            <b>Cluster Details for AC_ID: {acId || "N/A"}</b>
          </span>
          {/* First Chart */}
          <div className="col-md-6">
            <div className="card">
              <div className="card-body">
                <b>Monthly Consumption Cluster Graph</b>
                <Chart
                  options={{
                    ...chartOptions,
                    xaxis: { categories: chartData.map((d) => d.label) },
                  }}
                  series={[
                    {
                      name: "CONS Data",
                      data: chartData.map((d) => d.value),
                    },
                  ]}
                  type="line"
                  height={235}
                />
              </div>
            </div>
          </div>

          {/* Second Chart */}
          <div className="col-md-6">
            <div className="card">
              <div className="card-body">
                <b>Cluster Graph</b>
                <Chart
                  options={{
                    ...chartOptions,
                    xaxis: { categories: outChartData.map((d) => d.label) },
                  }}
                  series={[
                    {
                      name: "Out Data",
                      data: outChartData.map((d) =>
                        parseFloat(d.value).toFixed(2)
                      ),
                    },
                  ]}
                  type="line"
                  height={235}
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
};

export default ClusterModal;
